import { Component, useState, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ExclamationTriangleIcon, FlagIcon } from '@heroicons/react/24/outline';
import { useI18n } from '../i18n';
import { recordError } from '../diagnostics/buffers';
import { BugReportModal } from '../diagnostics/BugReportModal';

/**
 * Frontière d'erreur applicative. Capture les erreurs de rendu de l'arbre, les
 * enregistre (`recordError` source `boundary`, avec la pile de composants) et affiche
 * un écran de secours proposant de RECHARGER ou de SIGNALER UN BUG.
 *
 * DEUX portées :
 *   - `variant='full'` (défaut) : frontière APPLICATIVE, montée SOUS les providers
 *     thème + i18n mais AU-DESSUS du routeur/auth (cf. `main.tsx`) → l'écran de secours
 *     PLEIN ÉCRAN reste traduit et thémé même quand tout l'arbre a planté.
 *   - `variant='inline'` : frontière de ROUTE, montée AUTOUR de l'`<Outlet>` DANS la
 *     coquille (`AppChrome`). Le repli s'inscrit DANS la zone `main` → l'en-tête
 *     PERSISTANT (fil d'Ariane + navigation) reste VISIBLE et utilisable pour
 *     naviguer ailleurs. Un crash de page fait donc surface sur un repli
 *     diagnosticable au lieu d'une page blanche, sans tuer toute l'app.
 *
 * RÉCUPÉRATION : `resetKey` (typiquement la clé de navigation `useLocation().key`).
 * Quand elle change, la frontière se réinitialise → naviguer AILLEURS efface l'état
 * d'erreur et remonte l'arbre propre (le repli inline n'est pas « collant »).
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  /** `full` = plein écran (app) ; `inline` = dans `main`, chrome préservé (route). */
  variant?: 'full' | 'inline';
  /** Change → réinitialise la frontière (ex. clé de route pour récupérer en naviguant). */
  resetKey?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  message: string;
  /** Pile de composants React (via `componentDidCatch`) — surfacée pour diagnostic. */
  componentStack: string | null;
  /** Clé de reset observée quand la dernière erreur a été capturée. */
  seenResetKey: string | undefined;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      message: '',
      componentStack: null,
      seenResetKey: props.resetKey,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, message: error.message || 'Erreur de rendu' };
  }

  /**
   * Réinitialise la frontière quand la clé de reset change (navigation) : sans cela,
   * un repli inline resterait affiché même après avoir quitté la page fautive.
   */
  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    if (state.hasError && props.resetKey !== state.seenResetKey) {
      return { hasError: false, message: '', componentStack: null, seenResetKey: props.resetKey };
    }
    if (!state.hasError && props.resetKey !== state.seenResetKey) {
      return { seenResetKey: props.resetKey };
    }
    return null;
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // On journalise la pile d'appel de l'erreur ET la pile de COMPOSANTS React (qui
    // localise le composant fautif) → l'enveloppe « Signaler un bug » inclut de quoi
    // diagnostiquer une régression de rendu (source `boundary`).
    const parts = [error.stack, info.componentStack].filter(Boolean);
    const stack = parts.length > 0 ? parts.join('\n\n') : undefined;
    recordError(error.message || 'Erreur de rendu', 'boundary', stack);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <CrashScreen
          variant={this.props.variant ?? 'full'}
          message={this.state.message}
          componentStack={this.state.componentStack}
        />
      );
    }
    return this.props.children;
  }
}

/**
 * Écran de secours : traduit, thémé, avec report de bug et rechargement. En variante
 * `inline`, il s'inscrit dans le flux (pas de plein écran) → la coquille reste visible.
 */
function CrashScreen({
  variant,
  message,
  componentStack,
}: {
  variant: 'full' | 'inline';
  message: string;
  componentStack: string | null;
}): React.JSX.Element {
  const { t } = useI18n();
  const [reporting, setReporting] = useState(false);

  const wrapperClass =
    variant === 'full'
      ? 'flex min-h-screen flex-col items-center justify-center gap-4 bg-surface p-6 text-center text-fg'
      : 'flex flex-col items-center justify-center gap-4 rounded-lg border border-border bg-surface p-6 text-center text-fg';

  return (
    <div className={wrapperClass} role="alert">
      <ExclamationTriangleIcon className="h-12 w-12 text-danger" aria-hidden="true" />
      <h1 className="text-xl font-semibold">{t('bugReport.crashTitle')}</h1>
      <p className="max-w-md text-sm text-muted">
        {variant === 'inline' ? t('bugReport.crashHintInline') : t('bugReport.crashHint')}
      </p>
      {/* Message d'erreur brut : aide au diagnostic direct (l'enveloppe complète —
          message + pile de composants — est déjà capturée pour « Signaler un bug »). */}
      {message && (
        <p className="max-w-md break-words rounded-md bg-surface-2 px-3 py-2 font-mono text-xs text-muted">
          {message}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-center gap-2">
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-md border border-border px-4 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          {t('bugReport.reload')}
        </button>
        <button
          type="button"
          onClick={() => setReporting(true)}
          className="inline-flex items-center gap-2 rounded-md bg-brand-strong px-4 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          <FlagIcon className="h-5 w-5" aria-hidden="true" />
          {t('bugReport.open')}
        </button>
      </div>
      {/* La pile de composants n'est PAS rendue à l'écran (bruit) : elle vit dans
          l'enveloppe de diagnostic, jointe au report. `sr-only` la garde toutefois
          consultable/testable sans encombrer l'UI. */}
      {componentStack && <pre className="sr-only">{componentStack}</pre>}
      {reporting && <BugReportModal onClose={() => setReporting(false)} />}
    </div>
  );
}

/**
 * Frontière d'erreur de ROUTE — enveloppe l'`<Outlet>` de la coquille. Se réinitialise
 * automatiquement à chaque navigation (`useLocation().key`) → une page qui plante
 * affiche le repli diagnosticable SANS masquer l'en-tête, et quitter la page rétablit
 * un arbre propre. À utiliser DANS `AppChrome`, sous le routeur (d'où le hook).
 */
export function RouteErrorBoundary({ children }: { children: ReactNode }): React.JSX.Element {
  const location = useLocation();
  return (
    <ErrorBoundary variant="inline" resetKey={location.key}>
      {children}
    </ErrorBoundary>
  );
}
